// asyncOrchestrator.js — async steer/report channel for Hermes fan-out (arch-fable5 A4).
//
// Fable 5 doctrine: "Sub-agents that communicate asynchronously with the orchestrator
// outperform spawn-and-block" — the orchestrator isn't bottlenecked on the slowest child,
// long-lived children keep their context, and you "intervene if a sub-agent goes off track
// or is missing context". This pure core tracks per-child progress reports and decides WHEN
// to intervene; the React layer (runWiring/useAgentChannels) wires onChildEvent → ingest and
// a timer → detectDrift → steer.
//
// PURE: no react, no window, no Date.now (callers pass `now`). Worktree isolation, per-task
// checkpoints, and each child's honest-green are unchanged — A4 only adds the steer channel.

const TERMINAL = new Set(['done', 'failed', 'merged', 'terminated']);

/** Create orchestrator state for a set of child ids (more can be added via ingest). */
export function createOrchestrator(childIds = []) {
  const children = {};
  for (const id of childIds) {
    children[id] = { id, status: 'pending', lastSeen: null, steeredAt: null };
  }
  return { children };
}

function cloneChild(c) {
  return { id: c.id, status: c.status, lastSeen: c.lastSeen, steeredAt: c.steeredAt };
}

/**
 * Record a child progress report. Returns a NEW state (immutable). Unknown ids are added
 * (a child can report before it was registered). A terminal status freezes future steering.
 * @param {object} state
 * @param {{id:any, index?:number, state?:string}} event
 * @param {number} now - caller-supplied timestamp (ms)
 */
export function ingestChildEvent(state, event, now) {
  if (!event || event.id == null) return state;
  const children = {};
  for (const k of Object.keys(state.children)) children[k] = cloneChild(state.children[k]);
  const prev = children[event.id] || { id: event.id, status: 'pending', lastSeen: null, steeredAt: null };
  children[event.id] = {
    ...prev,
    status: typeof event.state === 'string' ? event.state : prev.status,
    lastSeen: Number.isFinite(now) ? now : prev.lastSeen,
  };
  return { children };
}

/**
 * Decide which children need intervention RIGHT NOW. The orchestrator never blocks on the
 * slowest child — it only flags the ones that drifted.
 * @returns {Array<{id:any, reason:('stalled'|'failed')}>}
 */
export function detectDrift(state, now, { stalledMs = 120000, reSteerMs = 120000 } = {}) {
  const out = [];
  for (const id of Object.keys(state.children)) {
    const c = state.children[id];
    if (TERMINAL.has(c.status) && c.status !== 'failed') continue;
    // don't re-steer the same child within reSteerMs of the last steer
    if (c.steeredAt != null && Number.isFinite(now) && now - c.steeredAt < reSteerMs) continue;
    if (c.status === 'failed') {
      out.push({ id: c.id, reason: 'failed' });
      continue;
    }
    if (c.status === 'running' && c.lastSeen != null && Number.isFinite(now) && now - c.lastSeen >= stalledMs) {
      out.push({ id: c.id, reason: 'stalled' });
    }
  }
  return out;
}

/** Mark a child as steered at `now` so detectDrift won't re-flag it immediately. Returns new state. */
export function markSteered(state, id, now) {
  if (state.children[id] == null) return state;
  const children = {};
  for (const k of Object.keys(state.children)) children[k] = cloneChild(state.children[k]);
  children[id] = { ...children[id], steeredAt: Number.isFinite(now) ? now : children[id].steeredAt };
  return { children };
}

/** True once every child has reached a terminal status — the merge can proceed. */
export function allTerminal(state) {
  const ids = Object.keys(state.children);
  if (ids.length === 0) return false;
  return ids.every((id) => TERMINAL.has(state.children[id].status));
}
